import { useEffect } from "react"
import { useForm, Controller } from "react-hook-form"
import { Input, PhoneInput } from "../../../../../components"
import styles from "./contact-information.module.scss"
import { validatePhoneNumber } from "../../../../../validators"
import { useSaveZambiaBusinessOwnerContactInfoMutation } from "../../../../../../../service/approval-service"
import { toast } from "react-toastify"
import { useSelector } from "react-redux"
import { RootState } from "../../../../../../../stores/store"

interface ContactFormValues {
  name: string
  phoneNumber: string
}

interface ContactInformationProps {
  onCancel: () => void;
  onContinue: () => void;
}

export const ContactInformation = ({ onCancel, onContinue }: ContactInformationProps) => {
  const creditInfo = useSelector((state: RootState) => state.getSolar.creditInfo);

  const [saveZambiaBusinessOwnerContactInfo, { isLoading: isSaving }] = useSaveZambiaBusinessOwnerContactInfoMutation();

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<ContactFormValues>({
    defaultValues: {
      name: "",
      phoneNumber: "",
    },
  })

  useEffect(() => {
    try {
      if (creditInfo?.businessOwnerData?.contactInformation != null) {
        reset({
          name: creditInfo.businessOwnerData.contactInformation.name ?? "",
          phoneNumber: creditInfo.businessOwnerData.contactInformation.phoneNumber ?? "",
        });
      }
    } catch (error) {
      toast.error("Failed to fetch contact information")
    }
  }, [creditInfo, reset]);

  const onSubmit = async (data: ContactFormValues) => {
    try {
      await saveZambiaBusinessOwnerContactInfo({ name: data.name, phoneNumber: data.phoneNumber }).unwrap();
      onContinue();
    } catch (error) {
      toast.error("Failed to submit contact information");
    }
  }

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.header}>
          <h2 className={styles.title}>Contact Information</h2>
          <p className={styles.subtitle}>Verify Company Contact Identity information</p>
        </div>

        <form className={styles.formContainer} onSubmit={handleSubmit(onSubmit)}>
          <Controller
            name="name"
            control={control}
            rules={{
              required: "Name is required",
              pattern: {
                value: /^[^\d]*$/,
                message: "Name cannot contain numbers"
              }
            }}
            render={({ field }) => (
              <Input
                type="text"
                placeholder="Name*"
                value={field.value}
                onChange={field.onChange}
                variant="solid"
                error={errors.name?.message}
              />
            )}
          />

          <Controller
            name="phoneNumber"
            control={control}
            rules={{
              required: "Phone number is required",
              validate: (value) => {
                return validatePhoneNumber(value);
              }
            }}
            render={({ field, fieldState }) => (
              <PhoneInput
                value={field.value}
                defaultCountry="zm"
                onChange={(phone) => {
                  field.onChange(phone)
                }}
                placeholder="Enter phone number"
                error={fieldState.error?.message}
                className={styles.phoneInput}
              />
            )}
          />

          <div className={styles.footer}>
            <div className={styles.footerInner}>
              <button
                type="button"
                className={styles.cancelBtn}
                onClick={onCancel}
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={styles.continueBtn}
                disabled={isSaving}
              >
                Continue
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}
